import { apiClient } from "@/services/apiClient";

export interface RucInfoResponse {
  valid: boolean;
  legalName?: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  phone?: string;
  email?: string;
  fechaInicio?: string;
}

export function getRucInfo(rut: string): Promise<RucInfoResponse> {
  return apiClient
    .get<RucInfoResponse>("/platform/tenants/ruc-info", {
      params: { rut: rut.trim() },
    })
    .then((res) => res.data);
}

export type PlatformTenantStatus =
  | "active"
  | "onboarding"
  /** Literal E claimed, nobody has reviewed it yet. Holding the free tier. */
  | "pending_approval"
  /** Literal E reviewed and refused. Blocked from emitting until they re-pick. */
  | "literal_e_rejected"
  | "pending_deletion";

export interface PlatformTenant {
  id: string;
  name: string;
  /** Present in list/detail; omitted in create response. */
  legalName?: string | null;
  rut: string;
  profileImage?: string | null;
  /** Present in list; omitted in create response. */
  createdAt?: string;
  /** Present in list response; omitted in getOne/update/create. */
  status?: PlatformTenantStatus;
  /** Present in list when Literal E was rejected; reason given by platform. */
  literalERejectionNote?: string | null;
  /** Per-tenant DGI environment. null = use system default. */
  dgiEnv?: "test" | "prod" | null;
  /**
   * Name (or email) of the accountant who onboarded this tenant. null/absent
   * = self / auto-onboarded. List response only.
   */
  accountantName?: string | null;
  /**
   * When the inactivity closure notice was emailed to this tenant. null = never
   * notified. The platform delete endpoint refuses removal until a notice has
   * been sent and the notice period has elapsed (unless overridden). List
   * response only.
   */
  closureNoticeSentAt?: string | null;
}

export interface PlatformTenantDetail extends PlatformTenant {
  address: string | null;
  updatedAt: string;
  language?: string;
  industry?: string | null;
  /** Per-tenant DGI environment. null = use system default. */
  dgiEnv?: "test" | "prod" | null;
  /** From DGI consultas: true = electronic issuer, false = not, null = not checked or unreachable. */
  emisorElectronico?: boolean | null;
  emisorElectronicoCheckedAt?: string | null;
  /** Set when the tenant was dado de baja (offboarded); null = active. */
  bajaAt?: string | null;
  bajaReason?: string | null;
}

export interface ListTenantsParams {
  page: number;
  limit: number;
  search?: string;
  status?: string;
}

export interface ListTenantsResponse {
  data: PlatformTenant[];
  total: number;
}

export interface CreateTenantPayload {
  name: string;
  rut: string;
  legalName?: string | null;
  address?: string | null;
  language?: string;
  industry?: string | null;
}

export interface UpdateTenantPayload {
  name?: string;
  legalName?: string | null;
  address?: string | null;
  language?: string;
  industry?: string | null;
  dgiEnv?: "test" | "prod" | null;
}

export function listTenants(
  params?: ListTenantsParams
): Promise<ListTenantsResponse> {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;
  return apiClient
    .get<ListTenantsResponse>("/platform/tenants", {
      params: {
        page,
        limit,
        ...(params?.search?.trim() && { search: params.search.trim() }),
        ...(params?.status && { status: params.status }),
      },
    })
    .then((res) => res.data);
}

export function createTenant(
  payload: CreateTenantPayload
): Promise<PlatformTenant> {
  return apiClient
    .post<PlatformTenant>("/platform/tenants", payload)
    .then((res) => res.data);
}

export function uploadTenantImage(
  id: string,
  file: File
): Promise<PlatformTenantDetail> {
  const formData = new FormData();
  formData.append("image", file);
  return apiClient
    .post<PlatformTenantDetail>(`/platform/tenants/${id}/upload-image`, formData)
    .then((res) => res.data);
}

export function getTenantById(id: string): Promise<PlatformTenantDetail> {
  return apiClient
    .get<PlatformTenantDetail>(`/platform/tenants/${id}`)
    .then((res) => res.data);
}

export interface TenantOnboardingOverview {
  id: string;
  name: string;
  legalName: string | null;
  rut: string;
  address: string | null;
  city: string | null;
  department: string | null;
  phone: string | null;
  language: string;
  industry: string | null;
  profession: string | null;
  profileImage: string | null;
  invoiceLogoImage: string | null;
  dgiEnv: "test" | "prod" | null;
  status: PlatformTenantStatus;
  onboardingCompletedAt: string | null;
  hasDgiCredentials: boolean;
  literalE: boolean | null;
  planId: string | null;
  step6AbitabAcknowledgedAt: string | null;
  hasDgiPortalUser: boolean;
  dgiPortalUserConfirmed: boolean;
  dgiPortalUser: {
    documentType: string;
    documentNumber: string;
    password: string;
    createdAt: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  certificateRequest: {
    id: string;
    status: string;
    idDigitalRequestNumber: string | null;
    createdAt: string;
  } | null;
  primaryContacts: Array<{ email: string; displayName: string }>;
  onboardingSituationChoice:
    | "scratch"
    | "issuer_need_regularization"
    | "already_issuer"
    | null;
  /** Raw `company.dgiAutoOnboardingProgress` blob — keys for each auto-flow
   *  step hold an ISO timestamp (done) or are absent (pending). Two reserved
   *  keys hold failure state: `_failures` (count per step) and `_failed`
   *  (terminal failure message per step). For manual-DGI tenants who clicked
   *  Configurar on the dashboard, `_failed.proveedor_software` will equal
   *  `"manual_intervention"`. */
  dgiAutoOnboardingProgress: Record<string, unknown> | null;
}

export const ONBOARDING_AUTO_STEP_KEYS = [
  "portal_user",
  "company_info",
  "cae_download",
  "proveedor_software",
] as const;

export type OnboardingAutoStepKey = (typeof ONBOARDING_AUTO_STEP_KEYS)[number];

export function stampOnboardingProgressStep(
  tenantId: string,
  step: OnboardingAutoStepKey,
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/platform/tenants/${tenantId}/onboarding-progress/stamp-step`,
      { step },
    )
    .then((res) => res.data);
}

export function clearOnboardingProgressFailure(
  tenantId: string,
  step: OnboardingAutoStepKey,
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/platform/tenants/${tenantId}/onboarding-progress/clear-failure`,
      { step },
    )
    .then((res) => res.data);
}

export function getTenantOnboardingOverview(
  id: string
): Promise<TenantOnboardingOverview> {
  return apiClient
    .get<TenantOnboardingOverview>(`/platform/tenants/${id}/onboarding-overview`)
    .then((res) => res.data);
}

export interface FiscaloConfigResult {
  accepted: boolean;
  idemisorSyncSessionId?: string;
  proveedorSoftwareSessionId?: string;
  message?: string;
}

/**
 * Root-scoped trigger for the merged "Configuración Fiscalo en DGI" flow
 * (idemisor_sync → proveedor_software) on a given tenant — the admin
 * equivalent of the accountant onboarding's one-click config button.
 */
export function startFiscaloConfigForTenant(
  tenantId: string,
): Promise<FiscaloConfigResult> {
  return apiClient
    .post<FiscaloConfigResult>(`/platform/tenants/${tenantId}/fiscalo-config`)
    .then((res) => res.data);
}

export interface FiscaloConfigRunning {
  running: boolean;
  step: "idemisor_sync" | "proveedor_software" | null;
}

export function getFiscaloConfigRunningForTenant(
  tenantId: string,
): Promise<FiscaloConfigRunning> {
  return apiClient
    .get<FiscaloConfigRunning>(
      `/platform/tenants/${tenantId}/fiscalo-config-running`,
    )
    .then((res) => res.data);
}

export function updateTenant(
  id: string,
  payload: UpdateTenantPayload
): Promise<PlatformTenantDetail> {
  return apiClient
    .patch<PlatformTenantDetail>(`/platform/tenants/${id}`, payload)
    .then((res) => res.data);
}

export interface DgiCredentialsStatus {
  hasCredentials: boolean;
  certificateValidFrom?: string;
  certificateValidTo?: string;
}

export function getDgiCredentials(
  tenantId: string
): Promise<DgiCredentialsStatus> {
  return apiClient
    .get<DgiCredentialsStatus>(
      `/platform/tenants/${tenantId}/dgi-credentials`
    )
    .then((res) => res.data);
}

export function uploadDgiCredentials(
  tenantId: string,
  file: File,
  idEmisor: number,
  passphrase?: string
): Promise<void> {
  const formData = new FormData();
  formData.append("p12", file);
  formData.append("idEmisor", String(idEmisor));
  if (passphrase != null && passphrase !== "") {
    formData.append("passphrase", passphrase);
  }
  return apiClient
    .post(`/platform/tenants/${tenantId}/dgi-credentials`, formData)
    .then(() => undefined);
}

export function removeDgiCredentials(tenantId: string): Promise<void> {
  return apiClient
    .delete(`/platform/tenants/${tenantId}/dgi-credentials`)
    .then(() => undefined);
}

export function downloadDgiCertificate(
  tenantId: string
): Promise<{ blob: Blob; filename: string }> {
  return apiClient
    .get(`/platform/tenants/${tenantId}/dgi-credentials/certificate`, {
      responseType: "blob",
    })
    .then((res) => {
      const disposition = res.headers["content-disposition"] ?? "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      return {
        blob: res.data as Blob,
        filename: match?.[1] ?? `${tenantId}.p12`,
      };
    });
}

export function revealDgiPassphrase(
  tenantId: string
): Promise<{ passphrase: string | null }> {
  return apiClient
    .get<{ passphrase: string | null }>(
      `/platform/tenants/${tenantId}/dgi-credentials/passphrase`
    )
    .then((res) => res.data);
}

export function approveLiteralE(tenantId: string): Promise<void> {
  return apiClient
    .post(`/platform/tenants/${tenantId}/literal-e/approve`)
    .then(() => undefined);
}

export function rejectLiteralE(
  tenantId: string,
  payload: { note: string }
): Promise<void> {
  return apiClient
    .post(`/platform/tenants/${tenantId}/literal-e/reject`, payload)
    .then(() => undefined);
}

export function confirmDeletionRequest(tenantId: string): Promise<void> {
  return apiClient
    .post(`/platform/tenants/${tenantId}/confirm-deletion`)
    .then(() => undefined);
}

/** Root only. Deletes a tenant that has not finished onboarding (setup). */
export function deleteInactiveSetupTenant(tenantId: string): Promise<void> {
  return apiClient
    .delete(`/platform/tenants/${tenantId}/inactive`)
    .then(() => undefined);
}

/** Root only: dar de baja a tenant (offboarding). Stops DGI ops + billing; data retained. */
export function darDeBajaTenant(
  tenantId: string,
  reason: string
): Promise<{ id: string; bajaAt: string | null; bajaReason: string | null }> {
  return apiClient
    .post<{ id: string; bajaAt: string | null; bajaReason: string | null }>(
      `/platform/tenants/${tenantId}/baja`,
      { reason }
    )
    .then((res) => res.data);
}

/** Root only: reactivar a tenant previously dado de baja (re-enable DGI ops). */
export function reactivarTenant(
  tenantId: string
): Promise<{ id: string; bajaAt: string | null }> {
  return apiClient
    .post<{ id: string; bajaAt: string | null }>(
      `/platform/tenants/${tenantId}/reactivar`
    )
    .then((res) => res.data);
}

export interface SyncRecibidasParams {
  dateFrom: string;
  dateTo: string;
}

export interface SyncRecibidasResponse {
  processed: number;
  inserted: number;
  updated: number;
}

export function syncRecibidas(
  tenantId: string,
  params: SyncRecibidasParams
): Promise<SyncRecibidasResponse> {
  return apiClient
    .post<SyncRecibidasResponse>(
      `/platform/tenants/${tenantId}/recibidas/sync`,
      {
        dateFrom: params.dateFrom.slice(0, 10),
        dateTo: params.dateTo.slice(0, 10),
      }
    )
    .then((res) => res.data);
}

export interface ImportResult {
  imported: number;
  merged: number;
  skipped: number;
  errors: { filename: string; message: string; code?: string }[];
}

export function importInvoicesXml(
  tenantId: string,
  files: File[]
): Promise<ImportResult> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return apiClient
    .post<ImportResult>(`/platform/tenants/${tenantId}/invoices/import`, formData)
    .then((res) => res.data);
}

export function importRecibidasXml(
  tenantId: string,
  files: File[]
): Promise<ImportResult> {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return apiClient
    .post<ImportResult>(`/platform/tenants/${tenantId}/recibidas/import`, formData)
    .then((res) => res.data);
}

function getDownloadFilenameFromHeaders(headers: {
  "content-disposition"?: string;
}): string | null {
  const value = headers["content-disposition"];
  if (!value) return null;
  const match = value.match(/filename="([^"]+)"/);
  return match ? match[1] : null;
}

/** Download ZIP of emitted CFEs for a tenant (platform). Triggers a file download in the browser. */
export function downloadExportEmittedCfesForTenant(tenantId: string): Promise<void> {
  return apiClient
    .get<Blob>(`/platform/tenants/${tenantId}/invoices/export/emitted`, {
      responseType: "blob",
    })
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers as { "content-disposition"?: string }) ??
        "CFE Emitidos.zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

/** Download ZIP of received CFEs for a tenant (platform). Triggers a file download in the browser. */
export function downloadExportRecibidasCfesForTenant(
  tenantId: string
): Promise<void> {
  return apiClient
    .get<Blob>(`/platform/tenants/${tenantId}/recibidas/export`, {
      responseType: "blob",
    })
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers as { "content-disposition"?: string }) ??
        "CFE Recibidos.zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

export interface PlatformTenantDocument {
  id: string;
  name: string;
  fileUrl: string;
  contentType: string;
  originalFilename: string;
  createdAt: string;
}

export function listTenantDocuments(
  tenantId: string
): Promise<PlatformTenantDocument[]> {
  return apiClient
    .get<PlatformTenantDocument[]>(`/platform/tenants/${tenantId}/documents`)
    .then((res) => res.data);
}

export function uploadTenantDocumentPlatform(
  tenantId: string,
  file: File,
  name?: string
): Promise<PlatformTenantDocument> {
  const formData = new FormData();
  formData.append("file", file);
  if (name != null && name.trim() !== "") {
    formData.append("name", name.trim());
  }
  return apiClient
    .post<PlatformTenantDocument>(
      `/platform/tenants/${tenantId}/documents`,
      formData
    )
    .then((res) => res.data);
}

export function updateTenantDocumentNamePlatform(
  tenantId: string,
  docId: string,
  name: string
): Promise<PlatformTenantDocument> {
  return apiClient
    .patch<PlatformTenantDocument>(
      `/platform/tenants/${tenantId}/documents/${docId}`,
      { name: name.trim() }
    )
    .then((res) => res.data);
}

export function deleteTenantDocumentPlatform(
  tenantId: string,
  docId: string
): Promise<void> {
  return apiClient
    .delete(`/platform/tenants/${tenantId}/documents/${docId}`)
    .then(() => undefined);
}

export interface SupportMessageItem {
  id: string;
  direction: "support" | "tenant";
  body: string;
  authorUserId: string | null;
  authorDisplayName: string | null;
  createdAt: string;
  emailSentAt: string | null;
}

export function listSupportMessages(
  tenantId: string
): Promise<SupportMessageItem[]> {
  return apiClient
    .get<SupportMessageItem[]>(
      `/platform/tenants/${tenantId}/support-messages`
    )
    .then((res) => res.data);
}

export function createSupportMessage(
  tenantId: string,
  message: string
): Promise<{ id: string; direction: string; body: string; createdAt: string }> {
  return apiClient
    .post<{ id: string; direction: string; body: string; createdAt: string }>(
      `/platform/tenants/${tenantId}/support-messages`,
      { message: message.trim() }
    )
    .then((res) => res.data);
}

/** CAE list item as returned by GET /platform/tenants/:id/cae (same shape as settings CAE list). */
export interface TenantCaeListItem {
  id: string;
  companyId: string;
  cfeType: string;
  series: string;
  rangeFrom: number;
  rangeTo: number;
  validFrom: string;
  validTo: string;
  nextNumber: number;
  authorizationNumber: number | null;
  caeEspecial: number | null;
  causalCaEEsp: number | null;
  lastUsedNumber: number;
  status: "active" | "exhausted" | "expired" | "not_yet_valid";
  expiringSoon: boolean;
  rangeRemaining: number;
  rangeLow: boolean;
  deletedAt: string | null;
}

export function listCaesForTenant(
  tenantId: string
): Promise<TenantCaeListItem[]> {
  return apiClient
    .get<TenantCaeListItem[]>(`/platform/tenants/${tenantId}/cae`)
    .then((res) => res.data);
}

export function addCaeForTenant(
  tenantId: string,
  file: File,
  isNew: boolean,
  lastUsedNumber?: number,
  cfeType?: string
): Promise<{ id: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("isNew", String(isNew));
  if (lastUsedNumber !== undefined && lastUsedNumber !== null) {
    formData.append("lastUsedNumber", String(lastUsedNumber));
  }
  if (cfeType !== undefined && cfeType !== "") {
    formData.append("cfeType", cfeType);
  }
  return apiClient
    .post<{ id: string }>(`/platform/tenants/${tenantId}/cae`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export function getCaeXmlForTenant(
  tenantId: string,
  caeId: string
): Promise<string> {
  return apiClient
    .get<string>(`/platform/tenants/${tenantId}/cae/${caeId}/xml`, {
      responseType: "text",
    })
    .then((res) => res.data);
}

export function downloadCaeForTenant(
  tenantId: string,
  caeId: string
): Promise<void> {
  return apiClient
    .get<Blob>(
      `/platform/tenants/${tenantId}/cae/${caeId}/download`,
      { responseType: "blob" }
    )
    .then((res) => {
      const blob = res.data;
      const filename =
        getDownloadFilenameFromHeaders(res.headers as {
          "content-disposition"?: string;
        }) ?? `CAE_${caeId}.xml`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
}

export function deleteCaeForTenant(
  tenantId: string,
  caeId: string
): Promise<void> {
  return apiClient
    .delete(`/platform/tenants/${tenantId}/cae/${caeId}`)
    .then(() => undefined);
}
